import { Controller } from "@hotwired/stimulus"

const STRIPE_SCRIPT_SRC = "https://js.stripe.com/v3/"
const CARD_FIELD_TARGETS = ["cardNumber", "cardExpiry", "cardCvc"]

export default class extends Controller {
  static targets = ["form", "cardNumber", "cardExpiry", "cardCvc", "cardErrors", "submit"]

  static values = {
    publishableKey: String,
  }

  connect() {
    this.disconnected = false

    this.loadStripe()
      .then(() => {
        if (this.disconnected || !this.element.isConnected) return
        this.mountElements()
      })
      .catch(() => {
        if (this.disconnected || !this.element.isConnected) return
        this.showCardError("Unable to load payment form. Please refresh and try again.", { focus: true })
      })
  }

  disconnect() {
    this.disconnected = true
    this.destroyElements()
    this.stripe = null
  }

  destroyElements() {
    this.cardNumberElement?.destroy()
    this.cardExpiryElement?.destroy()
    this.cardCvcElement?.destroy()
    this.cardNumberElement = null
    this.cardExpiryElement = null
    this.cardCvcElement = null
  }

  submit(event) {
    event.preventDefault()
    if (!this.stripe || !this.cardNumberElement) {
      this.showCardError("Payment form is still loading. Please wait a moment and try again.", {
        focus: true,
      })
      return
    }

    this.setBusy(true)

    this.stripe
      .createToken(this.cardNumberElement)
      .then((result) => {
        if (this.disconnected || !this.element.isConnected) return

        if (result.error) {
          this.showCardError(result.error.message, { focus: true })
          this.setBusy(false)
          return
        }

        this.appendStripeToken(result.token.id)
        this.formTarget.submit()
      })
      .catch(() => {
        if (this.disconnected || !this.element.isConnected) return
        this.showCardError("Unable to process payment. Please try again.", { focus: true })
        this.setBusy(false)
      })
  }

  loadStripe() {
    if (window.Stripe) return Promise.resolve()

    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${STRIPE_SCRIPT_SRC}"]`)
      if (existingScript) {
        if (
          window.Stripe ||
          existingScript.readyState === "complete" ||
          existingScript.readyState === "loaded"
        ) {
          resolve()
          return
        }
        existingScript.addEventListener("load", resolve, { once: true })
        existingScript.addEventListener("error", reject, { once: true })
        return
      }

      const script = document.createElement("script")
      script.src = STRIPE_SCRIPT_SRC
      script.async = true
      script.addEventListener("load", resolve, { once: true })
      script.addEventListener("error", reject, { once: true })
      document.head.appendChild(script)
    })
  }

  mountElements() {
    if (!this.publishableKeyValue) {
      this.showCardError("Unable to load payment form. Please refresh and try again.", { focus: true })
      return
    }

    // Failed payment re-renders remount this controller; tear down any leftovers first.
    this.destroyElements()

    this.stripe = window.Stripe(this.publishableKeyValue)
    const elements = this.stripe.elements()
    const style = this.elementStyle()

    this.cardNumberElement = this.mountField(elements, "cardNumber", this.cardNumberTarget, { style })
    this.cardExpiryElement = this.mountField(elements, "cardExpiry", this.cardExpiryTarget, {
      style,
      placeholder: "MM | YY",
    })
    this.cardCvcElement = this.mountField(elements, "cardCvc", this.cardCvcTarget, {
      style,
      placeholder: "123",
    })

    this.cardNumberElement?.focus?.()
  }

  mountField(elements, type, target, options) {
    const field = elements.create(type, options)
    field.mount(target)
    field.on("change", (event) => this.handleElementChange(event))
    return field
  }

  elementStyle() {
    return {
      base: {
        // Prefer resolved DS tokens; hex is light-theme token fallback only (Stripe iframes reject CSS vars).
        color: this.cssColor("--ds-text-primary", "#293346"), // --ds-gray-900
        fontFamily: '"DM Sans", sans-serif',
        fontSize: "16px",
        lineHeight: "24px",
        "::placeholder": {
          color: this.cssColor("--ds-text-secondary", "#636F7D"), // --ds-gray-800
        },
      },
      invalid: {
        color: this.cssColor("--ds-statuses-error", "#AD346A"), // --ds-pink-500
      },
    }
  }

  // Stripe Element iframes cannot read CSS variables; resolve tokens to rgb/hex first.
  cssColor(cssVar, fallback) {
    const probe = document.createElement("span")
    probe.style.color = `var(${cssVar})`
    document.body.appendChild(probe)
    const color = getComputedStyle(probe).color
    probe.remove()

    if (!color || color === "rgba(0, 0, 0, 0)" || color === "transparent" || color.includes("var(")) {
      return fallback
    }

    return color
  }

  handleElementChange(event) {
    this.showCardError(event.error?.message || "")
  }

  showCardError(message, { focus = false } = {}) {
    if (!this.hasCardErrorsTarget) return

    this.cardErrorsTarget.textContent = message
    this.setCardFieldsInvalid(Boolean(message))

    if (focus && message) {
      this.cardErrorsTarget.focus()
    }
  }

  setCardFieldsInvalid(invalid) {
    CARD_FIELD_TARGETS.forEach((name) => {
      const getter = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
      const targetGetter = `${name}Target`
      if (!this[getter]) return

      if (invalid) {
        this[targetGetter].setAttribute("aria-invalid", "true")
      } else {
        this[targetGetter].removeAttribute("aria-invalid")
      }
    })
  }

  setBusy(busy) {
    if (this.hasFormTarget) {
      this.formTarget.setAttribute("aria-busy", busy ? "true" : "false")
    }
    if (this.hasSubmitTarget) this.submitTarget.disabled = busy
  }

  appendStripeToken(tokenId) {
    const existing = this.formTarget.querySelector('input[name="stripeToken"]')
    if (existing) {
      existing.value = tokenId
      return
    }

    const hiddenInput = document.createElement("input")
    hiddenInput.type = "hidden"
    hiddenInput.name = "stripeToken"
    hiddenInput.value = tokenId
    this.formTarget.appendChild(hiddenInput)
  }
}
